import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Building2,
  ChevronRight,
  Pencil,
  Trash2,
  Upload,
  File,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatDate, generateId, formatFileSize } from '../lib/utils';
import Modal from '../components/Modal';
import type { Client, Stage, InvoiceMilestone } from '../types';
import { addWeeks, format } from 'date-fns';

// ────────────────────────────────────────────────
// Timeline auto-generation from contract/offer
// ────────────────────────────────────────────────
function generateTimeline(
  projectId: string,
  projectValue: number,
  startDate: Date,
  totalWeeks: number
): { stages: Omit<Stage, 'id'>[]; invoices: Omit<InvoiceMilestone, 'id'>[] } {
  const d = (offset: number) => format(addWeeks(startDate, offset), 'yyyy-MM-dd');

  const stages: Omit<Stage, 'id'>[] = [
    {
      projectId,
      order: 1,
      title: 'Projekti käivitamine & info kogumine',
      description:
        'Projekti seadistamine, kliendi materjalide, sisu, piltide ja brändiraamatu kogumine.',
      plannedStart: d(0),
      plannedEnd: d(2),
      status: 'not_started',
      completionPercent: 0,
      notes: '',
      blockerNote: '',
    },
    {
      projectId,
      order: 2,
      title: 'Disain',
      description:
        'Kujunduse loomine vastavalt brändile. Kuni 4 korrektuurivooru.',
      plannedStart: d(2),
      plannedEnd: d(Math.round(totalWeeks * 0.42)),
      status: 'not_started',
      completionPercent: 0,
      notes: '',
      blockerNote: '',
    },
    {
      projectId,
      order: 3,
      title: 'Arendustöö',
      description:
        'Kujunduse arendamine, lehtede programmeerimine, formulärid, mobiilivaade.',
      plannedStart: d(Math.round(totalWeeks * 0.42)),
      plannedEnd: d(Math.round(totalWeeks * 0.84)),
      status: 'not_started',
      completionPercent: 0,
      notes: '',
      blockerNote: '',
    },
    {
      projectId,
      order: 4,
      title: 'Testimine & käivitamine',
      description:
        'Kvaliteedikontroll, SEO seadistus, live keskkonda viimine, kliendi üleandmine.',
      plannedStart: d(Math.round(totalWeeks * 0.84)),
      plannedEnd: d(totalWeeks),
      status: 'not_started',
      completionPercent: 0,
      notes: '',
      blockerNote: '',
    },
  ];

  const half = Math.round(projectValue / 2);
  const invoices: Omit<InvoiceMilestone, 'id'>[] = [
    {
      projectId,
      relatedStageId: '', // filled in after stages created
      title: 'Ettemaks (50%) — töö alustamine',
      amount: half,
      percentage: 50,
      plannedSendDate: d(0),
      sentDate: '',
      paidDate: '',
      status: 'not_ready',
      notes: 'Ettemaks enne töö alustamist.',
    },
    {
      projectId,
      relatedStageId: '',
      title: 'Lõppmakse (50%) — projekti üleandmine',
      amount: projectValue - half,
      percentage: 50,
      plannedSendDate: d(totalWeeks),
      sentDate: '',
      paidDate: '',
      status: 'not_ready',
      notes: 'Tasuda pärast projekti lõpetamist ja live viimist.',
    },
  ];

  return { stages, invoices };
}

// ────────────────────────────────────────────────
// File upload mini-component
// ────────────────────────────────────────────────
function FileUploadSlot({
  label,
  file,
  onChange,
  onClear,
}: {
  label: string;
  file: File | null;
  onChange: (f: File) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-1.5">{label}</p>
      {file ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-brand-50 border border-brand-200 rounded-lg">
          <File size={14} className="text-brand-500 shrink-0" />
          <span className="text-xs text-gray-700 truncate flex-1">{file.name}</span>
          <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
          <button onClick={onClear} className="text-gray-400 hover:text-red-500 ml-1">×</button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-brand-300 hover:bg-brand-50/40 transition-colors">
          <Upload size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">Lae üles PDF / DOCX</span>
          <input
            ref={ref}
            type="file"
            className="hidden"
            accept=".pdf,.docx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange(f); e.target.value = ''; }}
          />
        </label>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────
// Add Client form (with doc uploads)
// ────────────────────────────────────────────────
function AddClientForm({
  onSave,
  onCancel,
}: {
  onSave: (
    client: Omit<Client, 'id' | 'createdAt'>,
    offerFile: File | null,
    contractFile: File | null,
    projectValue: number,
    projectWeeks: number,
    createProject: boolean
  ) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [offerFile, setOfferFile] = useState<File | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [projectValue, setProjectValue] = useState<number>(0);
  const [projectWeeks, setProjectWeeks] = useState<number>(12);
  const [createProject, setCreateProject] = useState(true);

  const hasDoc = offerFile || contractFile;
  const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-5">
      {/* Client info */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Kliendi andmed</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-600 block mb-1">Ettevõtte nimi *</label>
            <input
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              className={inputCls}
              placeholder="OÜ Näide"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Kontaktisiku nimi</label>
            <input
              value={form.contactPerson}
              onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))}
              className={inputCls}
              placeholder="Mari Tamm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">E-post</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              placeholder="mari@naide.ee"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-600 block mb-1">Telefon</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className={inputCls}
              placeholder="+372 5xxx xxxx"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-600 block mb-1">Märkused</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className={`${inputCls} resize-none`}
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Document uploads */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dokumendid</p>
        <div className="grid grid-cols-2 gap-3">
          <FileUploadSlot
            label="Pakkumus (PDF / DOCX)"
            file={offerFile}
            onChange={setOfferFile}
            onClear={() => setOfferFile(null)}
          />
          <FileUploadSlot
            label="Leping (PDF / DOCX)"
            file={contractFile}
            onChange={setContractFile}
            onClear={() => setContractFile(null)}
          />
        </div>
      </div>

      {/* Auto-generate project */}
      {hasDoc && (
        <div className={`rounded-xl border p-4 transition-colors ${createProject ? 'border-brand-200 bg-brand-50/40' : 'border-gray-100 bg-gray-50'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={createProject}
              onChange={(e) => setCreateProject(e.target.checked)}
              className="mt-0.5 rounded text-brand-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <Sparkles size={14} className={createProject ? 'text-brand-500' : 'text-gray-300'} />
                <span className={`text-sm font-semibold ${createProject ? 'text-brand-700' : 'text-gray-500'}`}>
                  Genereeri projekti ajaplaan dokumentidest
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Loob automaatselt projekti etapid ja arve vahe-eesmärgid lepingust.
              </p>
            </div>
          </label>

          {createProject && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Projekti maksumus (€)</label>
                <input
                  type="number"
                  value={projectValue || ''}
                  onChange={(e) => setProjectValue(Number(e.target.value))}
                  className={inputCls}
                  placeholder="nt 7500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Projekti kestus (nädalat)</label>
                <select
                  value={projectWeeks}
                  onChange={(e) => setProjectWeeks(Number(e.target.value))}
                  className={inputCls}
                >
                  <option value={8}>8 nädalat</option>
                  <option value={10}>10 nädalat</option>
                  <option value={12}>12 nädalat (vaikimisi)</option>
                  <option value={14}>14 nädalat</option>
                  <option value={16}>16 nädalat</option>
                  <option value={20}>20 nädalat</option>
                </select>
              </div>
            </div>
          )}

          {createProject && (
            <div className="mt-3 px-3 py-2.5 bg-white border border-brand-100 rounded-lg">
              <p className="text-xs font-medium text-gray-600 mb-2">Genereeritavad etapid:</p>
              <div className="space-y-1">
                {[
                  `Nädalad 1–2 — Projekti käivitamine & info kogumine`,
                  `Nädalad 3–${Math.round(projectWeeks * 0.42)} — Disain`,
                  `Nädalad ${Math.round(projectWeeks * 0.42) + 1}–${Math.round(projectWeeks * 0.84)} — Arendustöö`,
                  `Nädalad ${Math.round(projectWeeks * 0.84) + 1}–${projectWeeks} — Testimine & käivitamine`,
                ].map((line) => (
                  <div key={line} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <CheckCircle2 size={11} className="text-brand-400 shrink-0" />
                    {line}
                  </div>
                ))}
              </div>
              {projectValue > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-600 mb-1">Arvete vahe-eesmärgid:</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <CheckCircle2 size={11} className="text-yellow-400 shrink-0" />
                      Ettemaks 50% — {Math.round(projectValue / 2).toLocaleString()} € (projekti alguses)
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <CheckCircle2 size={11} className="text-yellow-400 shrink-0" />
                      Lõppmakse 50% — {(projectValue - Math.round(projectValue / 2)).toLocaleString()} € (projekti lõpus)
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          Tühista
        </button>
        <button
          onClick={() =>
            form.companyName.trim() &&
            onSave(form, offerFile, contractFile, projectValue, projectWeeks, !!(createProject && hasDoc))
          }
          className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium"
        >
          {hasDoc && createProject ? 'Lisa klient & genereeri ajaplaan' : 'Lisa klient'}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Edit Client form (simple)
// ────────────────────────────────────────────────
function EditClientForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: Client;
  onSave: (data: Partial<Client>) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    companyName: initial.companyName,
    contactPerson: initial.contactPerson,
    email: initial.email,
    phone: initial.phone,
    notes: initial.notes,
  });
  const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-600 block mb-1">Ettevõtte nimi *</label>
          <input value={form.companyName} onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Kontaktisik</label>
          <input value={form.contactPerson} onChange={(e) => setForm((f) => ({ ...f, contactPerson: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">E-post</label>
          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-600 block mb-1">Telefon</label>
          <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-600 block mb-1">Märkused</label>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={`${inputCls} resize-none`} rows={3} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Tühista</button>
        <button onClick={() => form.companyName.trim() && onSave(form)} className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium">Salvesta</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Success confirmation
// ────────────────────────────────────────────────
function CreatedConfirmation({
  clientName,
  projectCreated,
  onView,
  onClose,
}: {
  clientName: string;
  projectCreated: boolean;
  onView: () => void;
  onClose: () => void;
}) {
  return (
    <div className="text-center py-2">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 size={28} className="text-green-600" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">Klient lisatud!</h3>
      <p className="text-sm text-gray-500 mb-1">
        <span className="font-medium">{clientName}</span> on edukalt lisatud.
      </p>
      {projectCreated && (
        <div className="mt-3 px-4 py-3 bg-brand-50 border border-brand-100 rounded-xl text-sm text-brand-700">
          <Sparkles size={14} className="inline mr-1.5" />
          Projekt ja ajaplaan on automaatselt genereeritud lepingust.
        </div>
      )}
      <div className="flex gap-3 mt-6 justify-center">
        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
          Sulge
        </button>
        {projectCreated && (
          <button onClick={onView} className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium">
            Vaata projekti
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// Main Clients page
// ────────────────────────────────────────────────
export default function Clients() {
  const clients = useStore((s) => s.clients);
  const projects = useStore((s) => s.projects);
  const users = useStore((s) => s.users);
  const currentUser = useStore((s) => s.currentUser);
  const addClient = useStore((s) => s.addClient);
  const updateClient = useStore((s) => s.updateClient);
  const deleteClient = useStore((s) => s.deleteClient);
  const addProject = useStore((s) => s.addProject);
  const addStage = useStore((s) => s.addStage);
  const addInvoice = useStore((s) => s.addInvoice);
  const addDocument = useStore((s) => s.addDocument);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [created, setCreated] = useState<{ clientName: string; projectId: string | null } | null>(null);
  const [sort, setSort] = useState<{ col: 'createdAt' | 'companyName' | 'projects'; dir: 'asc' | 'desc' }>({ col: 'createdAt', dir: 'desc' });

  const projectCount = (clientId: string) =>
    projects.filter((p) => p.clientId === clientId).length;

  const toggleSort = (col: typeof sort.col) =>
    setSort((s) => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));

  const SortIcon = ({ col }: { col: typeof sort.col }) => (
    <span className={`ml-1 ${sort.col === col ? 'text-brand-500' : 'text-gray-300'}`}>
      {sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const filtered = clients
    .filter(
      (c) =>
        c.companyName.toLowerCase().includes(search.toLowerCase()) ||
        c.contactPerson.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (sort.col === 'companyName') return a.companyName.localeCompare(b.companyName) * dir;
      if (sort.col === 'projects') return (projectCount(a.id) - projectCount(b.id)) * dir;
      // createdAt
      return (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0) * dir;
    });

  const handleAddClient = (
    clientData: Omit<Client, 'id' | 'createdAt'>,
    offerFile: File | null,
    contractFile: File | null,
    projectValue: number,
    projectWeeks: number,
    shouldCreateProject: boolean
  ) => {
    // 1. Add client — Zustand set() is synchronous
    addClient(clientData);
    const newClient = useStore.getState().clients.find((c) => c.companyName === clientData.companyName);
    if (!newClient) { setShowAdd(false); return; }

    let newProjectId: string | null = null;

    if (shouldCreateProject) {
      const today = new Date();
      const pmUser = users.find((u) => u.role === 'pm' || u.role === 'sales_pm');
      const salesUser =
        users.find((u) => u.id === currentUser?.id && (u.role === 'sales' || u.role === 'sales_pm' || u.role === 'admin' || u.role === 'super_admin')) ??
        users.find((u) => u.role === 'sales' || u.role === 'sales_pm');

      // 2. Add project
      addProject({
        clientId: newClient.id,
        name: `${clientData.companyName} projekt`,
        description: '',
        value: projectValue || 0,
        currency: 'EUR',
        salesOwnerId: salesUser?.id ?? '',
        projectManagerId: pmUser?.id ?? '',
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(addWeeks(today, projectWeeks), 'yyyy-MM-dd'),
        status: 'active',
      });

      // Read project synchronously
      const newProject = useStore.getState().projects.find((p) => p.clientId === newClient.id);
      if (!newProject) { setShowAdd(false); return; }
      newProjectId = newProject.id;

      // 3. Generate and add stages
      const { stages, invoices } = generateTimeline(newProject.id, projectValue || 0, today, projectWeeks);
      stages.forEach((stage) => addStage({ ...stage, projectId: newProject.id }));

      // Read newly added stages
      const addedStages = useStore.getState().stages.filter((s) => s.projectId === newProject.id);
      const firstStage = addedStages.find((s) => s.order === 1);
      const lastStage = [...addedStages].sort((a, b) => b.order - a.order)[0];

      // 4. Add invoice milestones (linked to first/last stage)
      invoices.forEach((inv, idx) =>
        addInvoice({
          ...inv,
          projectId: newProject.id,
          relatedStageId: idx === 0 ? (firstStage?.id ?? '') : (lastStage?.id ?? ''),
        })
      );

      // 5. Add documents
      if (offerFile) {
        addDocument({
          projectId: newProject.id,
          type: 'offer',
          fileName: offerFile.name,
          fileSize: offerFile.size,
          version: 1,
          isLatest: true,
          uploadDate: new Date().toISOString(),
          uploadedById: currentUser?.id ?? '',
          extracted: true,
        });
      }
      if (contractFile) {
        addDocument({
          projectId: newProject.id,
          type: 'contract',
          fileName: contractFile.name,
          fileSize: contractFile.size,
          version: 1,
          isLatest: true,
          uploadDate: new Date().toISOString(),
          uploadedById: currentUser?.id ?? '',
          extracted: true,
        });
      }
    }

    setShowAdd(false);
    setCreated({ clientName: clientData.companyName, projectId: newProjectId });
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kliendid</h1>
          <p className="text-gray-500 text-sm mt-0.5">{clients.length} klienti kokku</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          <Plus size={15} /> Lisa klient
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Otsi kliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50">
              <th
                onClick={() => toggleSort('companyName')}
                className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
              >
                Ettevõte<SortIcon col="companyName" />
              </th>
              <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden md:table-cell">Kontaktisik</th>
              <th
                onClick={() => toggleSort('createdAt')}
                className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden lg:table-cell cursor-pointer hover:text-gray-600 select-none"
              >
                Lisatud<SortIcon col="createdAt" />
              </th>
              <th
                onClick={() => toggleSort('projects')}
                className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
              >
                Projektid<SortIcon col="projects" />
              </th>
              <th className="px-5 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Toimingud</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-gray-400 text-sm">
                  Kliente ei leitud
                </td>
              </tr>
            )}
            {filtered.map((client) => (
              <tr key={client.id} className="hover:bg-gray-50/50 transition-colors group">
                <td className="px-5 py-4">
                  <button
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-brand-500" />
                    </div>
                    <span className="font-medium text-gray-900 text-sm hover:text-brand-600 transition-colors">
                      {client.companyName}
                    </span>
                  </button>
                </td>
                <td className="px-5 py-4 text-sm text-gray-500 hidden md:table-cell">
                  {client.contactPerson || <span className="text-gray-300">—</span>}
                </td>
                <td className="px-5 py-4 text-sm text-gray-400 hidden lg:table-cell">
                  {formatDate(client.createdAt)}
                </td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md font-medium">
                    {projectCount(client.id)} projekti
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditing(client)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      title="Muuda"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Kustuta ${client.companyName}?`)) deleteClient(client.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Kustuta"
                    >
                      <Trash2 size={13} />
                    </button>
                    <button
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg"
                      title="Vaata"
                    >
                      <ChevronRight size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Client modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Lisa uus klient" size="lg">
        <AddClientForm
          onSave={handleAddClient}
          onCancel={() => setShowAdd(false)}
        />
      </Modal>

      {/* Edit Client modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Muuda klienti" size="lg">
        {editing && (
          <EditClientForm
            initial={editing}
            onSave={(data) => { updateClient(editing.id, data); setEditing(null); }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      {/* Success confirmation modal */}
      <Modal
        open={!!created}
        onClose={() => setCreated(null)}
        title=""
        size="sm"
      >
        {created && (
          <CreatedConfirmation
            clientName={created.clientName}
            projectCreated={!!created.projectId}
            onView={() => {
              if (created.projectId) navigate(`/projects/${created.projectId}`);
              else {
                const cl = clients.find((c) => c.companyName === created.clientName);
                if (cl) navigate(`/clients/${cl.id}`);
              }
              setCreated(null);
            }}
            onClose={() => setCreated(null)}
          />
        )}
      </Modal>
    </div>
  );
}
